import { useId, useState } from 'react';

/**
 * The chart pieces the dashboard needs, and no more.
 *
 * Hand-rolled SVG rather than a charting library: three shapes, no runtime
 * dependency, no 200 kB added to a bundle a dispatcher loads on a slow
 * connection, and full control over the accessibility rules below.
 *
 * **Colour.** The two series hues were checked with a CVD validator rather than
 * chosen by eye — blue and green separate at ΔE 21 for normal vision and 16+
 * under every simulated deficiency, in both light and dark steps.
 *
 * Good/warning/critical are handled deliberately differently. Amber and red
 * cannot be separated far enough for a colourblind reader at these lightnesses
 * — the honest fix is not a different amber but never relying on the colour:
 * SLA state is shown as a labelled table row, never as adjacent colour-only
 * marks in a chart.
 *
 * Single-series charts use one hue throughout. A bar per category in a
 * different colour encodes nothing and reads as a rainbow.
 */

export const SERIES = {
  primary: { light: '#0454A9', dark: '#3E8AD4', label: 'Created' },
  secondary: { light: '#1D8660', dark: '#2BA378', label: 'Resolved' },
};

/** Charts sit on the card surface and inherit the theme through CSS variables. */
const GRID = 'currentColor';

/**
 * The dark step is chosen by CSS, not JavaScript, so switching theme needs no
 * re-render and no colour is duplicated between the stylesheet and here.
 */
function seriesColour(which: 'primary' | 'secondary'): string {
  return `var(--chart-${which})`;
}

// ─── Line chart ─────────────────────────────────────────────────────────────

export type VolumePoint = { date: string; created: number; resolved: number };

/**
 * Two series over time, with a crosshair and tooltip.
 *
 * Both series are direct-labelled at their last point as well as appearing in
 * the legend, so identity never rests on colour alone.
 */
export function VolumeChart({ data, height = 200 }: { data: VolumePoint[]; height?: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const gradientId = useId();

  if (data.length === 0) return <Empty>No tickets in this period.</Empty>;

  const width = 720;
  const pad = { top: 12, right: 56, bottom: 24, left: 32 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const max = Math.max(1, ...data.flatMap((d) => [d.created, d.resolved]));
  const x = (i: number) => pad.left + (i / Math.max(1, data.length - 1)) * plotW;
  const y = (v: number) => pad.top + plotH - (v / max) * plotH;

  const path = (key: 'created' | 'resolved') =>
    data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(' ');

  const area = `${path('created')} L${x(data.length - 1)},${pad.top + plotH} L${pad.left},${pad.top + plotH} Z`;
  const ticks = [0, Math.round(max / 2), max];
  const point = hover === null ? null : data[hover];

  return (
    <figure className="m-0">
      <div className="mb-2 flex flex-wrap gap-4 text-xs">
        <LegendItem colour={seriesColour('primary')}>Created</LegendItem>
        <LegendItem colour={seriesColour('secondary')}>Resolved</LegendItem>
      </div>

      <div className="relative">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full"
          role="img"
          aria-label={`Tickets created and resolved over ${data.length} days`}
          onMouseLeave={() => setHover(null)}
          onMouseMove={(e) => {
            const box = e.currentTarget.getBoundingClientRect();
            const rel = ((e.clientX - box.left) / box.width) * width;
            const i = Math.round(((rel - pad.left) / plotW) * (data.length - 1));
            setHover(i >= 0 && i < data.length ? i : null);
          }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={seriesColour('primary')} stopOpacity="0.18" />
              <stop offset="100%" stopColor={seriesColour('primary')} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Recessive grid — present enough to read a value against, quiet
              enough not to compete with the data. */}
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={pad.left}
                x2={width - pad.right}
                y1={y(t)}
                y2={y(t)}
                stroke={GRID}
                strokeOpacity="0.12"
              />
              <text x={pad.left - 8} y={y(t) + 4} textAnchor="end" className="fill-current text-[10px] opacity-50">
                {t}
              </text>
            </g>
          ))}

          <path d={area} fill={`url(#${gradientId})`} />
          <path d={path('created')} fill="none" stroke={seriesColour('primary')} strokeWidth="2" />
          <path d={path('resolved')} fill="none" stroke={seriesColour('secondary')} strokeWidth="2" />

          {/* Direct labels at the last point, so the legend is not the only key. */}
          <text
            x={width - pad.right + 6}
            y={y(data[data.length - 1]!.created) + 4}
            className="text-[10px] font-medium"
            fill={seriesColour('primary')}
          >
            Created
          </text>
          <text
            x={width - pad.right + 6}
            y={y(data[data.length - 1]!.resolved) + 4}
            className="text-[10px] font-medium"
            fill={seriesColour('secondary')}
          >
            Resolved
          </text>

          {hover !== null && point && (
            <g>
              <line
                x1={x(hover)}
                x2={x(hover)}
                y1={pad.top}
                y2={pad.top + plotH}
                stroke={GRID}
                strokeOpacity="0.35"
              />
              {/* A surface-coloured ring keeps the marker readable wherever it
                  lands, including on top of the other line. */}
              <circle cx={x(hover)} cy={y(point.created)} r="4.5" fill={seriesColour('primary')} stroke="var(--chart-surface)" strokeWidth="2" />
              <circle cx={x(hover)} cy={y(point.resolved)} r="4.5" fill={seriesColour('secondary')} stroke="var(--chart-surface)" strokeWidth="2" />
            </g>
          )}

          <text x={pad.left} y={height - 6} className="fill-current text-[10px] opacity-50">
            {formatDay(data[0]!.date)}
          </text>
          <text x={width - pad.right} y={height - 6} textAnchor="end" className="fill-current text-[10px] opacity-50">
            {formatDay(data[data.length - 1]!.date)}
          </text>
        </svg>

        {hover !== null && point && (
          <div
            className="pointer-events-none absolute -translate-x-1/2 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs shadow-card"
            style={{ left: `${(x(hover) / width) * 100}%`, top: 0 }}
          >
            <div className="font-medium">{formatDay(point.date)}</div>
            <div className="text-muted-foreground">
              {point.created} in · {point.resolved} out
            </div>
          </div>
        )}
      </div>
    </figure>
  );
}

// ─── Horizontal bars ────────────────────────────────────────────────────────

/**
 * Ranked magnitudes. Horizontal because the labels are words — "Cable stolen
 * near Nyamache junction" does not fit under a vertical bar at any width.
 *
 * One hue for every bar: this is one series, and colouring each category
 * differently would encode nothing.
 */
export function BarList({
  data,
  max: maxOverride,
  limit = 8,
  format = (n: number) => String(n),
}: {
  data: { label: string; count: number }[];
  max?: number;
  limit?: number;
  format?: (n: number) => string;
}) {
  if (data.length === 0) return <Empty>Nothing to show.</Empty>;

  const rows = data.slice(0, limit);
  const max = maxOverride ?? Math.max(1, ...rows.map((r) => r.count));

  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li key={row.label} className="grid grid-cols-[1fr_auto] items-center gap-x-3 text-sm">
          <span className="truncate" title={row.label}>
            {humanise(row.label)}
          </span>
          <span className="tabular-nums font-medium">{format(row.count)}</span>
          <div className="col-span-2 h-2 rounded-full bg-muted">
            <div
              className="h-2 rounded-full"
              style={{
                width: `${Math.max(2, (row.count / max) * 100)}%`,
                background: 'var(--chart-primary)',
              }}
            />
          </div>
        </li>
      ))}
      {data.length > limit && (
        <li className="pt-1 text-xs text-muted-foreground">
          and {data.length - limit} more
        </li>
      )}
    </ul>
  );
}

// ─── Compliance meter ───────────────────────────────────────────────────────

/**
 * A single proportion. Not a pie: one number against a target reads faster as a
 * bar, and a two-slice pie is the classic example of a chart that is worse than
 * the number it contains.
 */
export function ComplianceMeter({ percent, target = 90 }: { percent: number; target?: number }) {
  const met = percent >= target;
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-semibold tabular-nums">{percent}%</span>
        <span className={`text-xs font-medium ${met ? 'text-secondary-700' : 'text-danger-700'}`}>
          {met ? 'at or above target' : `below the ${target}% target`}
        </span>
      </div>
      <div className="relative mt-2 h-2.5 rounded-full bg-muted">
        <div
          className="h-2.5 rounded-full"
          style={{
            width: `${Math.min(100, percent)}%`,
            background: met ? 'var(--chart-good)' : 'var(--chart-critical)',
          }}
        />
        {/* The target as a mark on the track, so the number has something to
            be judged against without reading the caption. */}
        <div
          className="absolute top-[-3px] h-[17px] w-0.5 bg-foreground/60"
          style={{ left: `${target}%` }}
          title={`Target ${target}%`}
        />
      </div>
    </div>
  );
}

// ─── Shared ─────────────────────────────────────────────────────────────────

function LegendItem({ colour, children }: { colour: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <span className="h-2 w-2 rounded-full" style={{ background: colour }} aria-hidden="true" />
      {children}
    </span>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{children}</p>;
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' });
}

/** ON_SITE → On site. Status codes are for the database, not the reader. */
export function humanise(value: string): string {
  if (!/^[A-Z0-9_]+$/.test(value)) return value;
  const words = value.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function formatDuration(ms: number | null): string {
  if (ms === null || ms <= 0) return '—';
  const hours = ms / 3_600_000;
  if (hours < 1) return `${Math.round(ms / 60_000)} min`;
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${Math.round(hours / 24)} days`;
}
